import { ChangeDetectorRef, Component, inject, OnDestroy } from '@angular/core'
import { ModalHeaderComponent } from '../modal-header/modal-header.component'
import { ModalFooterComponent } from '../modal-footer/modal-footer.component'
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap'
import { ModalBodyComponent } from '../modal-body/modal-body.component'
import { firstValueFrom, map, Subscription, tap } from 'rxjs'
import { FieldColumnsComponent } from '../../field-columns/field-columns.component'
import { LabeledPropertyComponent } from '../../labeled-property/labeled-property.component'
import { AsyncPipe } from '@angular/common'
import { ServicePointIdReference } from 'src/app/shared/models/referenceData'
import { BoolDisplayPipe } from 'src/app/shared/pipes/boolDisplay.pipe'
import { ServicePointFilter } from 'src/app/shared/services/filter/service-point-filter'
import {
  ServicePointManagementService,
} from '../../service-point-management/service-point-management.service'
import { ServicePointSelectorComponent } from '../../service-point-selector/service-point-selector.component'
import { SelectorComponent } from '../../selector/selector.component'
import { FlagFilter } from 'src/app/shared/services/filter/flag-filter'
import { ServicePointEffectiveDatedAttribute, UpsertFlagsRequest } from '../../service-point-management/model'
import { Filter, FilterService } from 'src/app/shared/services/filter/filter.service'
import { DateRangeFilter, DateRangeFilterStateValue } from 'src/app/shared/services/filter/date-range-filter'
import { DateRangeFilterPickerComponent } from '../../date-range-filter-picker/date-range-filter-picker.component'
import { Option } from '../../../shared/models/general'

@Component({
  imports: [
    ModalHeaderComponent,
    ModalBodyComponent,
    ModalFooterComponent,
    FieldColumnsComponent,
    LabeledPropertyComponent,
    ServicePointSelectorComponent,
    DateRangeFilterPickerComponent,
    SelectorComponent,
    AsyncPipe,
  ],
  selector: 'app-create-service-point-flag-modal',
  styles: `
  .only-md {
    display: none;
    @media (min-width: 768px) and (max-width: 991px) {
      display: block;
    }
  }`,
  template: `
    <div app-modal-header>{{ actionText }} Service Point Flag</div>
    <div app-modal-body>
      <div app-field-columns>
        @if(servicePoint('service_point_uid')) {
        <div app-labeled-property label="Service Point">
          <div
            app-service-point-selector
            (valueChange)="handleServicePoint($event, 'service_point_uid')"
            [selectedServicePoints]="servicePoint('service_point_uid').servicePoints"
            [slim]="true"
            [config]="servicePoint('service_point_uid').config"
            [allowPartial]="false"
          ></div>
        </div>
        }
        <br class="only-md"/>
        @if(dateRange('effective_date')) {
        <div app-labeled-property label="Effective Date">
          <div dateRangeFilterPicker
            [value]="dateRange('effective_date')"
            (valueChange)="handleDate($event, 'effective_date')"
            [showOperation]="dateRange('effective_date').config.showOperation ?? false"
            [showRange]="dateRange('effective_date').config.showRange ?? false"
            [showTime]="dateRange('effective_date').config.showTime ?? false"
            [showSeconds]="dateRange('effective_date').config.showSeconds ?? false"
          ></div>
        </div>
        }
        @if(dateRange('termination_date')) {
        <div app-labeled-property label="Termination Date">
          <div dateRangeFilterPicker
            [value]="dateRange('termination_date')"
            (valueChange)="handleDate($event, 'termination_date')"
            [showOperation]="dateRange('termination_date').config.showOperation ?? false"
            [showRange]="dateRange('termination_date').config.showRange ?? false"
            [showTime]="dateRange('termination_date').config.showTime ?? false"
            [showSeconds]="dateRange('termination_date').config.showSeconds ?? false"
          ></div>
        </div>
        }
      </div>
      <hr />
      <p>Provide flags for the segment. You must provide all attributes.</p>
      <div app-field-columns>
        @for(flag of flags; track flag.name) {
        <div app-labeled-property [label]="flag.display">
          <div
            app-selector
            [selected]="toFlagOption(flag.name)"
            [options]="flagOptions"
            [slim]="true"
            (selectedChange)="handleBool($event, flag.name)"
          ></div>
        </div>
        }
      </div>
    </div>
    <div app-modal-footer>
      <button
        class="action-btn primary"
        [disabled]="submitDisabled$ | async"
        (click)="submit()"
      >
        {{ actionText }}
      </button>
      <button class="action-btn" (click)="onCancel()">Close</button>
    </div>
  `,
})
export class CreateServicePointFlagModalComponent implements OnDestroy {
  activeModal = inject(NgbActiveModal)
  filterService = inject(FilterService)
  servicePointManagementService = inject(ServicePointManagementService)
  changeDetectorRef = inject(ChangeDetectorRef)

  state: Record<string, Filter> = {}
  flagOptions: Option<boolean | null>[] = [
    { value: null, display: 'None Selected' },
    { value: true, display: new BoolDisplayPipe().transform(true) },
    { value: false, display: new BoolDisplayPipe().transform(false) },
  ]
  flags = [
    {
      name: 'mdma_subtract_from_adjusted_system_load_flag',
      display: 'MDMA Subtract From Adjusted System Load',
    },
    {
      name: 'mdma_add_to_adjusted_system_load_flag',
      display: 'MDMA Add To Adjusted System Load',
    },
    {
      name: 'mdma_aggregation_exclusion_flag',
      display: 'MDMA Aggregation Exclusion',
    },
    {
      name: 'mdma_weather_sensitive_override_flag',
      display: 'MDMA Weather Sensitive Override',
    },
    {
      name: 'tbill_subtract_from_adjusted_system_load_flag',
      display: 'TBill Subtract From Adjusted System Load',
    },
    {
      name: 'tbill_add_to_adjusted_system_load_flag',
      display: 'TBill Add To Adjusted System Load',
    },
    {
      name: 'tbill_aggregation_exclusion_flag',
      display: 'TBill Aggregation Exclusion',
    },
    {
      name: 'tbill_weather_sensitive_flag',
      display: 'TBill Weather Sensitive',
    },
    {
      name: 'bmg_flag',
      display: 'BMG',
    },
  ]
  submitDisabled$ = this.filterService.state(CreateServicePointFlagModalComponent.name).pipe(
    map((state) => {
      const service_point_uid = (
        state['service_point_uid'] as ServicePointFilter | undefined
      )?.state.servicePoints[0]
      const effective_date = (state['effective_date'] as DateRangeFilter | undefined)
        ?.state.value as Date
      const termination_date = (
        state['termination_date'] as DateRangeFilter | undefined
      )?.state.value

      const flag_vals = this.flags.map((flag) =>
        (state[flag.name] as FlagFilter | undefined)?.state.value === undefined,
      )

      const required = [
        service_point_uid,
        effective_date,
        termination_date,
        flag_vals.some(Boolean),
      ]
      return !required.every(Boolean)
    }),
  )

  private subscription = new Subscription()
  private action: 'CREATE' | 'CLONE' | 'EDIT'

  get actionText(): string {
    switch (this.action) {
      case 'CREATE':
        return 'Create'
      case 'CLONE':
        return 'Clone'
      case 'EDIT':
        return 'Edit'
      default:
        return 'UNKNOWN'
    }
  }

  private onSuccess: () => void = () => void 0

  init(
    action: typeof this.action,
    data: ServicePointEffectiveDatedAttribute | undefined,
    onSuccess: () => void = () => void 0,
  ) {
    this.action = action
    this.onSuccess = onSuccess
    this.changeDetectorRef.detectChanges()

    const { dispatch } = this.filterService
    dispatch(FilterService.InitFiltersAction({
      fieldGroup: CreateServicePointFlagModalComponent.name,
      fields: {
        'service_point_uid': new ServicePointFilter({
          servicePoints: data && ['CLONE', 'EDIT'].includes(action) ? [{
            service_point_uid: data.service_point_uid, 
            service_point_id: data.service_point_id,
          }]: [],
          config: {
            constant: true,
            meta: {
              fieldGroup: CreateServicePointFlagModalComponent.name,
              fieldName: 'service_point_uid',
              fieldDisplay: 'Service Point',
            },
          },
        }, dispatch),
        'effective_date': new DateRangeFilter({
          value:  data && action === 'EDIT' ? data.effective_date: null,
          operation:  data && action === 'EDIT' ? 'eq' : null,
          config: {
            constant: true,
            showOperation: false,
            showRange: false,
            showTime: false,
            showSeconds: false,
            meta: {
              fieldGroup: CreateServicePointFlagModalComponent.name,
              fieldName: 'effective_date',
              fieldDisplay: 'Effective Date',
            },
          },
        }, dispatch),
        'termination_date': new DateRangeFilter({
          value:  data && action === 'EDIT' ? data.termination_date: null,
          operation:  data && action === 'EDIT' ? 'eq' : null,
          config: {
            constant: true,
            showOperation: false,
            showRange: false,
            showTime: false,
            showSeconds: false,
            meta: {
              fieldGroup: CreateServicePointFlagModalComponent.name,
              fieldName: 'termination_date',
              fieldDisplay: 'Termination Date',
            },
          },
        }, dispatch),
        ...this.flags.reduce((acc, f) => ({
          ...acc,
          [f.name]: new FlagFilter({
            value: data && ['CLONE', 'EDIT'].includes(action) ? !!data[f.name as keyof typeof data]: null,
            config: {
              meta: {
                fieldGroup: CreateServicePointFlagModalComponent.name,
                fieldName: f.name,
                fieldDisplay: f.display,
              },
            },
          }, dispatch),
        }), {}),
      },
    }))
    this.subscription.add(this.filterService.state(CreateServicePointFlagModalComponent.name).subscribe(state => this.state = state))

    this.changeDetectorRef.detectChanges()
  }

  dateRange(key: string) {
    return (this.state[key] as DateRangeFilter)?.state
  }

  servicePoint(key: string) {
    return (this.state[key] as ServicePointFilter)?.state
  }

  toFlagOption(key: string): Option<boolean | null> {
    const filter = this.state[key] as FlagFilter | undefined
    switch(filter?.state.value) {
      case null:
      case undefined:
        return this.flagOptions[0]
      case true: return this.flagOptions[1]
      case false: return this.flagOptions[2]
    }
  }

  ngOnDestroy(): void {
    this.subscription.unsubscribe()
  }

  onCancel() {
    this.activeModal.dismiss()
  }

  handleServicePoint(
    ref: ServicePointIdReference,
    field: string,
  ) {
    this.filterService.dispatch(
      ServicePointFilter.AddServicePointsAction({
        servicePoints: [ref],
      },
      {
        fieldGroup: CreateServicePointFlagModalComponent.name,
        fieldName: field as string,
      }),
    )
  }

  handleDate(val: DateRangeFilterStateValue, field: string) {
    this.filterService.dispatch(
      DateRangeFilter.SetDateRangeFilterValueAction(val, {
        fieldGroup: CreateServicePointFlagModalComponent.name,
        fieldName: field as string,
      }),
    )
  }

  handleBool(value: Option<boolean | null>, field: string) {
    this.filterService.dispatch(
      FlagFilter.SetFlagFilterValueAction({
        value: value.value ?? null,
      }, {
        fieldGroup: CreateServicePointFlagModalComponent.name,
        fieldName: field,
      }),
    )
  }

  async submit() {
    const service_point = (
      this.state['service_point_uid'] as ServicePointFilter | undefined
    )!.state.servicePoints[0]
    const effective_date = (
      this.state['effective_date'] as DateRangeFilter | undefined
    )?.state.value as Date
    const termination_date = (
      this.state['termination_date'] as DateRangeFilter | undefined
    )?.state.value as Date
    const flag_vals = this.flags.map((flag) => ({
      [flag.name]: (this.state[flag.name] as FlagFilter | undefined)?.state.value,
    }))
    await firstValueFrom(
      this.servicePointManagementService
        .upsertFlags(service_point.service_point_id, {
          effective_date,
          termination_date,
          ...flag_vals.reduce((acc, next) => ({ ...acc, ...next }), {}),
        } as UpsertFlagsRequest)
        .pipe(
          tap(() => {
            this.onSuccess()
            this.activeModal.dismiss()
          }),
        ),
    )
  }
}
